
import React, { useMemo } from "react";
import { Sankey, Rectangle, ResponsiveContainer, Tooltip } from "recharts";
import { SANKEY_COLORS, HEATMAP_COLORS } from "../../constants/chartConstants";
import {
  getUserClassColor,
  transformSankeyDataMultiColumn,
  getHeatmapColor,
} from "../../utils/chartUtils";
import { CustomTooltip } from "./CommonComponents";


/**
 * Custom Sankey Node Component
 */
export const CustomSankeyNode = ({
  x,
  y,
  width,
  height,
  index,
  payload,
  containerWidth,
  heatmapMode = false,
  maxValue = 1,
}) => {
  const isSource = x < containerWidth / 2;

  let color;
  if (heatmapMode) {
    color = getHeatmapColor(payload.value, maxValue, false);
  } else if (payload.userClass) {
    color = getUserClassColor(payload.userClass, "node");
  } else {
    color = isSource
      ? SANKEY_COLORS.source[index % SANKEY_COLORS.source.length]
      : SANKEY_COLORS.target[index % SANKEY_COLORS.target.length];
  }

  return (
    <g>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        fillOpacity="0.9"
      />
      <text
        x={isSource ? x - 10 : x + width + 10}
        y={y + height / 2}
        textAnchor={isSource ? "end" : "start"}
        fill="#374151"
        fontSize="12"
        fontWeight="500"
      >
        {payload.name}
      </text>
      {(payload.userClass || heatmapMode) && (
        <text
          x={isSource ? x - 10 : x + width + 10}
          y={y + height / 2 + 14}
          textAnchor={isSource ? "end" : "start"}
          fill="#9ca3af"
          fontSize="9"
          fontStyle="italic"
        >
          {heatmapMode ? payload.value?.toLocaleString() : payload.userClass}
        </text>
      )}
    </g>
  );
};

/**
 * Custom Sankey Link Component
 */
export const CustomSankeyLink = ({
  sourceX,
  targetX,
  sourceY,
  targetY,
  sourceControlX,
  targetControlX,
  linkWidth,
  index,
  payload,
  heatmapMode = false,
  maxValue = 1,
}) => {
  let strokeColor;
  if (heatmapMode) {
    strokeColor = getHeatmapColor(payload.value, maxValue, true);
  } else if (payload?.userClass) {
    strokeColor = getUserClassColor(payload.userClass, "link");
  } else {
    strokeColor = SANKEY_COLORS.link;
  }

  return (
    <path
      d={`
        M${sourceX},${sourceY}
        C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
      `}
      stroke={strokeColor}
      strokeWidth={linkWidth}
      fill="none"
      className="transition-all duration-200 hover:opacity-80"
      style={{
        cursor: "pointer",
        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
      }}
    />
  );
};

/**
 * Heatmap Legend Component
 */
export const HeatmapLegend = ({ maxValue }) => {
  const { gradient, thresholds } = HEATMAP_COLORS;

  return (
    <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded border border-gray-200">
      <span className="text-sm font-medium text-gray-700 mr-2">
        Traffic Intensity:
      </span>
      <div className="flex items-center gap-1">
        {gradient.map((color, index) => (
          <div key={index} className="flex flex-col items-center">
            <div
              className="w-12 h-4 rounded"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs text-gray-600 mt-1">
              {index === 0
                ? "Low"
                : index === gradient.length - 1
                  ? "High"
                  : Math.round(thresholds[index] * 100) + "%"}
            </span>
          </div>
        ))}
      </div>
      <span className="text-xs text-gray-500 ml-2">
        (Max: {maxValue?.toLocaleString()})
      </span>
    </div>
  );
};

/**
 * Enhanced Sankey Chart with Multi-Column Support
 * 
 * @param {Object} columnMapping - Can be string, array, or function
 *   String: 'source_col'
 *   Array: ['col1', 'col2'] - combines with separator
 *   Function: (item) => 'custom value'
 */
export const SankeyChart = ({
  data,
  compact,
  height,
  chartMargin,
  responsiveProps,
  tooltipProps,
  sortBy = "name",
  showHeatmap = false,
  columnMapping = null,
  sourceLabel = "IN",
  targetLabel = "OUT",
}) => {
  const normalizedData = useMemo(() => {
    if (!columnMapping) {
      return data; // Already in standard format
    }

    return data.map(item => {
      return {
        source: extractValue(item, columnMapping.source),
        target: extractValue(item, columnMapping.target),
        value: extractValue(item, columnMapping.value),
        userClass: columnMapping.userClass
          ? extractValue(item, columnMapping.userClass)
          : null,
      };
    });
  }, [data, columnMapping]);

  const sankeyData = transformSankeyDataMultiColumn(normalizedData, sortBy);
  const maxValue = Math.max(
    ...sankeyData.links.map((link) => link.value),
    ...sankeyData.nodes.map((node) => node.value || 0)
  );

  const NodeComponent = (props) => (
    <CustomSankeyNode
      {...props}
      heatmapMode={showHeatmap}
      maxValue={maxValue}
    />
  );

  const LinkComponent = (props) => (
    <CustomSankeyLink
      {...props}
      heatmapMode={showHeatmap}
      maxValue={maxValue}
    />
  );

  const margin = useMemo(
    () => chartMargin || { top: 28, right: 28, bottom: 16, left: 28 },
    [chartMargin]
  );

  return (
    <>
      {showHeatmap && !compact && <HeatmapLegend maxValue={maxValue} />}

      {/* Source/Target Labels */}
      {!compact && (
        <div className="flex justify-between items-center mb-2 px-4">
          <div className="text-sm font-semibold text-blue-600">
            {sourceLabel}
          </div>
          <div className="text-sm font-semibold text-green-600">
            {targetLabel}
          </div>
        </div>
      )}

      <ResponsiveContainer {...responsiveProps}>
        <Sankey
          data={sankeyData}
          node={NodeComponent}
          link={LinkComponent}
          nodePadding={compact ? 20 : 50}
          margin={margin}
          nodeWidth={compact ? 10 : 15}
        >
          <Tooltip
            content={<CustomTooltip dataFormat="sankey" />}
            {...tooltipProps}
          />
        </Sankey>
      </ResponsiveContainer>
    </>
  );
};

/**
 * Helper function to extract value from item using various mapping types
 * 
 * @param {Object} item - Data item
 * @param {string|Array|Function} mapping - How to extract value
 * @returns {string|number} - Extracted value
 */
function extractValue(item, mapping) {
  if (!mapping) return null;

  if (typeof mapping === 'string') {
    return item[mapping];
  }

  if (Array.isArray(mapping)) {
    if (mapping.length === 1) {
      return item[mapping[0]];
    }

    const separator = mapping.separator || ' - '; 
    return mapping
      .filter(col => typeof col === 'string')
      .map(col => item[col])
      .filter(val => val !== null && val !== undefined)
      .join(separator);
  }

  if (typeof mapping === 'object' && mapping.columns) {
    const separator = mapping.separator || ' - ';
    return mapping.columns
      .map(col => item[col])
      .filter(val => val !== null && val !== undefined)
      .join(separator);
  }

  if (typeof mapping === 'function') {
    return mapping(item);
  }

  return null;
}